import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { CheckboxList, CheckboxListItem } from "../checkbox-list/checkbox-list";
import { RadioButton } from "../radio-button/radio-button";
import "./analysis-feedback-form.scss";

export interface AnalysisFeedbackOption {
  /** Unique value used as the option identifier in callbacks. */
  value: string;
  /** Display label for the option. */
  label: string;
  /** When true, the option is disabled and cannot be toggled. */
  disabled?: boolean;
}

export interface AnalysisFeedbackPayload {
  /** Selected option values, in the order they appear. */
  selectedValues: string[];
  /** Trimmed comments text. */
  comments: string;
}

export type AnalysisFeedbackSelectionMode = "checkbox" | "radio";

export interface AnalysisFeedbackFormProps {
  /** Title shown above the option list. */
  title?: React.ReactNode;
  /** Available options rendered as a checkbox or radio list. */
  options: AnalysisFeedbackOption[];
  /**
   * Whether options allow multi-select (`"checkbox"`, default) or single-select (`"radio"`).
   * In `"radio"` mode, `selectedValues` / `defaultSelectedValues` are still used but limited to one entry.
   */
  selectionMode?: AnalysisFeedbackSelectionMode;
  /**
   * Controlled selected values. When provided, the component becomes controlled.
   * Pair with `onSelectedValuesChange` to update.
   */
  selectedValues?: string[];
  /** Initial selected values for uncontrolled usage. */
  defaultSelectedValues?: string[];
  /** Called whenever the selected values change. */
  onSelectedValuesChange?: (values: string[]) => void;
  /** Label for the comments field. Defaults to "Comments". */
  commentsLabel?: React.ReactNode;
  /** Placeholder for the comments textarea. */
  commentsPlaceholder?: string;
  /** Controlled value for the comments textarea. */
  comments?: string;
  /** Initial comments value for uncontrolled usage. */
  defaultComments?: string;
  /** Called whenever the comments value changes. */
  onCommentsChange?: (value: string) => void;
  /** Maximum number of characters allowed in the comments textarea. */
  commentsMaxLength?: number;
  /** When true, hides the comments field entirely. */
  hideComments?: boolean;
  /** When true, the comments field is required for submission. Defaults to false. */
  commentsRequired?: boolean;
  /** When true, requires at least one option to be selected before submitting. Defaults to true. */
  requireSelection?: boolean;
  /** Label for the primary submit button. Defaults to "Save". */
  submitLabel?: React.ReactNode;
  /** Label for the secondary cancel button. Defaults to "Cancel". */
  cancelLabel?: React.ReactNode;
  /** When true, hides the cancel button. */
  hideCancel?: boolean;
  /** When true, both action buttons are disabled. */
  disabled?: boolean;
  /** When true, the submit button shows a loading state and is disabled. */
  isSubmitting?: boolean;
  /** Called when the form is submitted with valid data. */
  onSubmit?: (payload: AnalysisFeedbackPayload) => void;
  /** Called when the cancel button is clicked. */
  onCancel?: () => void;
  /** Override the form background color. */
  backgroundColor?: string;
  /** Additional class names applied to the root element. */
  className?: string;
  /** Optional id used as a prefix for child element ids. */
  id?: string;
}

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const AnalysisFeedbackForm: React.FC<AnalysisFeedbackFormProps> = ({
  title = "Which of the analysis is wrong",
  options,
  selectionMode = "checkbox",
  selectedValues,
  defaultSelectedValues,
  onSelectedValuesChange,
  commentsLabel = "Comments",
  commentsPlaceholder = "Please describe what went wrong with the AI analysis...",
  comments,
  defaultComments,
  onCommentsChange,
  commentsMaxLength,
  hideComments = false,
  commentsRequired = false,
  requireSelection = true,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  hideCancel = false,
  disabled = false,
  isSubmitting = false,
  onSubmit,
  onCancel,
  backgroundColor,
  className = "",
  id,
}) => {
  const generatedId = useId();
  const idPrefix = id ?? `analysis-feedback-${generatedId}`;

  const isSelectionControlled = selectedValues !== undefined;
  const isCommentsControlled = comments !== undefined;

  const [internalSelected, setInternalSelected] = useState<string[]>(
    defaultSelectedValues ?? []
  );
  const [internalComments, setInternalComments] = useState<string>(
    defaultComments ?? ""
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const previousControlledSelected = useRef(selectedValues);
  useEffect(() => {
    if (
      isSelectionControlled &&
      selectedValues !== undefined &&
      !arraysEqual(previousControlledSelected.current ?? [], selectedValues)
    ) {
      previousControlledSelected.current = selectedValues;
    }
  }, [isSelectionControlled, selectedValues]);

  const activeSelected = isSelectionControlled
    ? selectedValues ?? []
    : internalSelected;
  const activeComments = isCommentsControlled ? comments ?? "" : internalComments;

  const updateSelected = (next: string[]) => {
    if (!isSelectionControlled) setInternalSelected(next);
    onSelectedValuesChange?.(next);
  };

  const updateComments = (next: string) => {
    if (!isCommentsControlled) setInternalComments(next);
    onCommentsChange?.(next);
  };

  const handleCheckboxListChange = (nextItems: CheckboxListItem[]) => {
    if (disabled) return;
    const next = nextItems.filter((item) => item.checked).map((item) => item.id);
    updateSelected(next);
    if (validationError) setValidationError(null);
  };

  const handleRadioChange = (nextValue: string) => {
    if (disabled) return;
    updateSelected([nextValue]);
    if (validationError) setValidationError(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || isSubmitting) return;

    const trimmedComments = activeComments.trim();

    if (requireSelection && activeSelected.length === 0) {
      setValidationError("Please select at least one option.");
      return;
    }

    if (commentsRequired && trimmedComments.length === 0) {
      setValidationError("Please add a comment before saving.");
      return;
    }

    setValidationError(null);
    onSubmit?.({
      selectedValues: activeSelected,
      comments: trimmedComments,
    });
  };

  const handleCancel = () => {
    if (disabled) return;
    setValidationError(null);
    onCancel?.();
  };

  const rootStyle = useMemo<React.CSSProperties | undefined>(
    () =>
      backgroundColor
        ? ({
            ["--analysis-feedback-form-bg" as string]: backgroundColor,
            backgroundColor,
          } as React.CSSProperties)
        : undefined,
    [backgroundColor]
  );

  const submitDisabled = disabled || isSubmitting;
  const cancelDisabled = disabled;

  return (
    <form
      className={`analysis-feedback-form ${className}`.trim()}
      style={rootStyle}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="analysis-feedback-form__section">
        {title && <p className="analysis-feedback-form__title">{title}</p>}
        <div className="analysis-feedback-form__options">
          {selectionMode === "radio" ? (
            <RadioButton
              name={`${idPrefix}-options`}
              options={options.map((option) => ({
                label: option.label,
                value: option.value,
                disabled: option.disabled,
              }))}
              value={activeSelected[0]}
              onChange={handleRadioChange}
              disabled={disabled}
              direction="vertical"
              selectedColor="#0f0f0f"
            />
          ) : (
            <CheckboxList
              items={options.map<CheckboxListItem>((option) => ({
                id: option.value,
                label: option.label,
                checked: activeSelected.includes(option.value),
                disabled: disabled || option.disabled,
              }))}
              onSelectionChange={handleCheckboxListChange}
              showSelectAll={false}
              width="100%"
              paddingX={0}
              paddingY={0}
              rowPaddingY={0.375}
              checkboxLabelGap={0.5}
              backgroundColor="transparent"
              ariaLabel={typeof title === "string" ? title : "Feedback options"}
            />
          )}
        </div>
      </div>

      {!hideComments && (
        <div className="analysis-feedback-form__field">
          <label
            className="analysis-feedback-form__field-label"
            htmlFor={`${idPrefix}-comments`}
          >
            {commentsLabel}
          </label>
          <textarea
            id={`${idPrefix}-comments`}
            className="analysis-feedback-form__textarea"
            placeholder={commentsPlaceholder}
            value={activeComments}
            disabled={disabled}
            maxLength={commentsMaxLength}
            onChange={(event) => {
              updateComments(event.target.value);
              if (validationError) setValidationError(null);
            }}
          />
        </div>
      )}

      {validationError && (
        <span className="analysis-feedback-form__error" role="alert">
          {validationError}
        </span>
      )}

      <div className="analysis-feedback-form__actions">
        <button
          type="submit"
          className="analysis-feedback-form__button analysis-feedback-form__button--primary"
          disabled={submitDisabled}
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
        {!hideCancel && (
          <button
            type="button"
            className="analysis-feedback-form__button analysis-feedback-form__button--secondary"
            disabled={cancelDisabled}
            onClick={handleCancel}
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </form>
  );
};

export default AnalysisFeedbackForm;
